import React from 'react';
import { Link } from 'react-router-dom';
import { Landmark, CalendarDays, Eye, Handshake, Award, Megaphone } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Footer from '../components/Footer';
import { useReveal } from '../hooks/useReveal';
import { IMAGES } from '../mock';

const BENEFITS = [
  {
    num: '01',
    tag: 'VOICE',
    icon: Landmark,
    title: 'Gain a voice in Government',
    body: 'The AHCC takes on the tough issues and works together with local government to ensure the voice of small business is properly represented. It also helps protect the principles of free enterprise and aids businesses in being treated fairly.',
  },
  {
    num: '02',
    tag: 'EVENTS',
    icon: CalendarDays,
    title: 'Chamber events and programs',
    body: 'Chamber events and programs provide members with great opportunities to network, meet new people and expand your prospect base. Chamber events are fun, effective and help members meet potential customers, clients and vendors.',
  },
  {
    num: '03',
    tag: 'VISIBILITY',
    icon: Eye,
    title: 'Increase your visibility in the community',
    body: 'As a member of the Chamber, you will be listed in the Chamber publications, including a highlighted listing in the highly regarded Community Guide. We also feature Chamber businesses on social media, newsletter and on the Chamber website. You can also grow your business by advertising with the Chamber and sponsoring events.',
  },
  {
    num: '04',
    tag: 'CONTACTS',
    icon: Handshake,
    title: 'Make business contacts',
    body: "The Chamber's fundamental mission is to generate more business activity for the community. The Chamber initiates more business-to-business commerce and more opportunities for networking and connecting local professionals than is available through most other local organizations.",
  },
  {
    num: '05',
    tag: 'CREDIBILITY',
    icon: Award,
    title: 'Bring credibility to your business',
    body: "You can increase positive perception among consumers and business owners when you're identified as a member of the AHCC. Make your products and services known to the entire community, locally and globally.",
  },
  {
    num: '07',
    tag: 'PROMOTION',
    icon: Megaphone,
    title: 'Promotion and publicity',
    body: 'With a Chamber membership, you can reach potential clients through member-exclusive advertising and opportunities for business-to-business advertising and publicity. This includes discounted ads in our highly valued Community Guide.',
  },
];

function BenefitRow({ b, index }) {
  const [ref, inView] = useReveal();
  const Icon = b.icon;
  return (
    <div ref={ref} className={`reveal ${inView ? 'in-view' : ''}`} style={{ animationDelay: `${(index % 2) * 0.08}s` }}>
      <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start py-12 border-b border-neutral-200/70">
        <div className="md:col-span-3 flex items-center gap-4">
          <span className="font-serif-ah text-[#4a9cd4]/25 text-[68px] leading-none">{b.num}</span>
          <div className="w-14 h-14 rounded-full bg-[#eef4f8] flex items-center justify-center shrink-0">
            <Icon size={26} className="text-[#4a9cd4]" />
          </div>
        </div>
        <div className="md:col-span-9">
          <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-3">
            {b.num} &mdash; {b.tag}
          </p>
          <h3 className="font-serif-ah text-[30px] md:text-[36px] text-[#2f2f2f] leading-tight mb-4">{b.title}</h3>
          <p className="font-sans-ah text-[16px] text-[#555] font-300 leading-[1.9] max-w-[760px]">{b.body}</p>
        </div>
      </div>
    </div>
  );
}

export default function BenefitsPage() {
  return (
    <>
      <PageBanner
        title="Benefits of Membership"
        crumb="MEMBERSHIP"
        subtitle="Explore the possibilities by taking advantage of the benefits of a Chamber Membership. Join today and let your voice be heard."
        image={IMAGES.joinImg}
      />

      <section className="bg-[#f8f6f2] py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <p className="font-sans-ah tracking-nav text-[12px] text-[#4a9cd4] font-500 mb-6">WHY JOIN THE CHAMBER?</p>
          <h2 className="font-serif-ah text-[36px] md:text-[48px] text-[#2f2f2f] leading-tight mb-8">
            Working together, we help your business grow
          </h2>
          <p className="font-sans-ah text-[17px] text-[#555] font-300 leading-[1.9]">
            Joining your area's Chamber of Commerce can be a smart move for your business. Businesses that participate
            assist with promotion of the Almaguin Highlands and our collective businesses. Working together, we can help
            your own business grow and prosper. Here are some of the reasons why you should join the Almaguin Highlands
            Chamber of Commerce.
          </p>
        </div>
      </section>

      <section className="bg-white py-6 md:py-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          {BENEFITS.map((b, i) => (
            <BenefitRow key={b.num} b={b} index={i} />
          ))}
        </div>
      </section>

      <section className="bg-[#1f2c33] py-20">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <h2 className="font-serif-ah text-white text-[34px] md:text-[46px] mb-5">Ready to let your voice be heard?</h2>
          <p className="font-sans-ah text-white/70 text-[16px] font-300 mb-8">
            Become a member of the Almaguin Highlands Chamber of Commerce today.
          </p>
          <Link to="/membership" className="btn-ah-solid inline-block font-sans-ah text-[13px] px-10 py-4 rounded-sm">
            JOIN TODAY
          </Link>
        </div>
      </section>
      <Footer />
    </>
  );
}
